import { useState, useEffect } from 'react'
import { Modal, Form, Button } from 'react-bootstrap'
import { t } from '../locale'
import { apiRequest, showKAlert } from '../helpers'
import { ensureEnoughCredit } from '../helpers/credit.helper'

type RegisterTypeOption = string

type OptionsPayload = {
  ok: boolean
  message: { variant: 'success' | 'danger'; content: string }
  court_options?: string[]
  register_types?: string[]
}

/* Komponenta modala */
type Props = {
  show: boolean
  onClose: () => void
  clientId: string | null | undefined
  onSuccess?: () => void
}

export default function AmlOrderModal({ show, onClose, clientId, onSuccess }: Props) {
  const [court, setCourt] = useState('') // dropdown (label)
  const [registerNumber, setRegisterNumber] = useState('') // text input
  const [registerType, setRegisterType] = useState<RegisterTypeOption>('') // dropdown

  const [courtOptions, setCourtOptions] = useState<string[]>([])
  const [registerTypes, setRegisterTypes] = useState<string[]>([])
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!show) return
    let aborted = false

    const load = async () => {
      try {
        setLoadingOptions(true)
        const res: OptionsPayload = await apiRequest(
          `${process.env.REACT_APP_API_URL}/aml/options`,
          'GET'
        )

        if (!res?.ok) {
          showKAlert(
            res?.message?.variant ?? 'danger',
            res?.message?.content ?? t('failedToLoadOptions')
          )
          return
        }

        if (!aborted) {
          setCourtOptions(res.court_options ?? [])
          setRegisterTypes(res.register_types ?? [])
        }
      } finally {
        if (!aborted) setLoadingOptions(false)
      }
    }

    load()
    return () => {
      aborted = true
    }
  }, [show])

  const validate = () => {
    if (!clientId) {
      showKAlert('danger', t('clientIdMissing'))
      return false
    }
    if (!court) {
      showKAlert('danger', t('courtIsRegistered'))
      return false
    }
    if (!registerType) {
      showKAlert('danger', t('registerTypeIsRequired'))
      return false
    }
    if (!registerNumber.trim() || isNaN(Number(registerNumber))) {
      showKAlert('danger', t('registerNumberInvalid'))
      return false
    }
    return true
  }

  const submit = async () => {
    if (!validate()) return
    if (!(await ensureEnoughCredit('handelsregister'))) return
    setLoading(true)
    try {
      const res = await apiRequest(`${process.env.REACT_APP_API_URL}/aml/order`, 'POST', {
        clientId,
        court, // šaljemo LABEL (npr. "Traunstein")
        registerNumber: String(registerNumber).trim(),
        registerType, // npr. "HRB"
      })

      if (!res?.ok) {
        showKAlert(
          res?.message?.variant ?? 'danger',
          res?.message?.content ?? t('failedToPlaceOrder')
        )
        return
      }

      showKAlert(res.message.variant, res.message.content)
      onClose()
      onSuccess?.()
    } catch (_e: any) {
      showKAlert('danger', t('failedToCommunicateServer'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('handelsregisterOrder')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* COURT */}
          <Form.Group className='mb-3'>
            <Form.Label>{t('Court')}</Form.Label>
            <Form.Select
              className='w-100'
              value={court}
              onChange={e => setCourt(e.target.value)}
              disabled={loadingOptions || !courtOptions.length}
            >
              <option value='' disabled>
                {t('selectCourt')}
              </option>
              {courtOptions.map(opt => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* REGISTER NUMBER */}
          <Form.Group className='mb-3'>
            <Form.Label>{t('registerNumberAml')}</Form.Label>
            <Form.Control
              type='text'
              value={registerNumber}
              onChange={e => setRegisterNumber(e.target.value)}
              placeholder={t('registerNumberAml')}
            />
          </Form.Group>

          {/* REGISTER TYPE */}
          <Form.Group className='mb-3'>
            <Form.Label>{t('registerType')}</Form.Label>
            <Form.Select
              className='w-100'
              value={registerType}
              onChange={e => setRegisterType(e.currentTarget.value as string)}
              disabled={loadingOptions || !registerTypes.length}
            >
              <option value='' disabled>
                {t('selectRegisterType')}
              </option>
              {registerTypes.map(rt => (
                <option key={rt} value={rt}>
                  {rt}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose} disabled={loading || loadingOptions}>
          {t('cancel')}
        </Button>
        <Button variant='primary' onClick={submit} disabled={loading || loadingOptions}>
          {loading ? t('ordering') : t('Order')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
